import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, TextField, Typography, Alert } from '@mui/material';
import { validateDiscountCode } from '../../services/discountService';

const formatCents = (cents) => `$${(cents / 100).toFixed(2)}`;

const DiscountCodeInput = ({ items, shippingOptionId, shippingCents, email, applied, onApply, onRemove }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  const handleApply = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setChecking(true);
    setError('');
    try {
      const result = await validateDiscountCode({
        code: code.trim(), items, shippingOptionId, shippingCents, email,
      });
      if (result.valid) {
        onApply(result);
        setCode('');
      } else {
        // Specific reasons, not "invalid code" — "spend $12 more" converts,
        // a dead end does not.
        setError(result.message || "That code isn't valid.");
      }
    } finally {
      setChecking(false);
    }
  };

  if (applied) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert
          severity="success"
          action={<Button size="small" onClick={onRemove}>Remove</Button>}
        >
          <strong>{applied.code}</strong> applied
          {applied.type === 'free_shipping'
            // Free shipping covers the standard rate only, so on an upgraded
            // option there is still a balance to pay. Claiming "free shipping"
            // there would contradict the shipping line in the summary.
            ? (applied.shippingCents > 0
              ? ` — ${formatCents(Math.max(shippingCents - applied.shippingCents, 0))} off shipping`
              : ' — free shipping')
            : applied.discountCents > 0 ? ` — ${formatCents(applied.discountCents)} off` : ''}
        </Alert>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleApply} sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>Have a discount code?</Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          id="discount-code"
          label="Discount code"
          size="small"
          fullWidth
          value={code}
          onChange={(e) => setCode(e.target.value)}
          error={Boolean(error)}
        />
        <Button type="submit" variant="outlined" disabled={checking || !code.trim()}>
          {checking ? 'Checking...' : 'Apply'}
        </Button>
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

DiscountCodeInput.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    quantity: PropTypes.number,
  })).isRequired,
  shippingOptionId: PropTypes.string,
  shippingCents: PropTypes.number,
  email: PropTypes.string,
  applied: PropTypes.shape({
    code: PropTypes.string,
    type: PropTypes.string,
    discountCents: PropTypes.number,
    shippingCents: PropTypes.number,
  }),
  onApply: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

DiscountCodeInput.defaultProps = {
  shippingOptionId: null,
  shippingCents: 0,
  email: null,
  applied: null,
};

export default DiscountCodeInput;
